/**
 * MermaidBlock — a ```mermaid fence in the notebook page editor (FRIDAY-67).
 *
 * The fence stays the source of truth: the page is stored as markdown, the
 * diagram is drawn in the browser. This node is what a mermaid fence
 * becomes when a page is loaded, and what it is written back as when the
 * page is saved.
 *
 * It is a separate node (not an extension of the code block) so that:
 *   - ordinary code blocks keep their exact current behaviour, and
 *   - only a mermaid fence gets the diagram node view.
 *
 * The node is registered BEFORE StarterKit so its parse rule wins: a
 * `<pre>` whose `<code>` child carries the mermaid language becomes a
 * MermaidBlock, every other `<pre>` falls through to the code block.
 */

import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import type { MarkdownNodeSpec } from 'tiptap-markdown'
import { MermaidBlockView } from '@/components/notebook/MermaidBlockView'

export const MERMAID_LANGUAGE = 'mermaid'

/** The diagram the slash menu drops into a page. */
export const MERMAID_STARTER = 'flowchart TD\n  A[Start] --> B[End]'

export const MermaidBlock = Node.create({
  name: 'mermaidBlock',
  group: 'block',
  content: 'text*',
  marks: '',
  // A code block: whitespace is kept, marks are not allowed.
  code: true,
  defining: true,

  parseHTML() {
    return [
      {
        tag: 'pre',
        preserveWhitespace: 'full',
        // `false` means "this rule does not apply" — ProseMirror then tries
        // the next rule, which is the ordinary code block.
        getAttrs: (element) => (isMermaidFence(element as HTMLElement) ? {} : false),
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'pre',
      mergeAttributes(HTMLAttributes, { 'data-mermaid-block': '' }),
      ['code', { class: `language-${MERMAID_LANGUAGE}` }, 0],
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(MermaidBlockView)
  },

  addStorage() {
    const spec: MarkdownNodeSpec = {
      serialize(state, node) {
        state.write(`\`\`\`${MERMAID_LANGUAGE}\n`)
        // The fence holds plain text — it is not escaped, the same way the
        // code block writes its source.
        state.text(node.textContent, false)
        state.ensureNewLine()
        state.write('```')
        state.closeBlock(node)
      },
      parse: {
        updateDOM(element) {
          // markdown-it ends a fence with a newline inside the <code>.
          // The code block extension strips it for every <pre>; we do the
          // same so the mermaid source never carries a trailing newline.
          element.innerHTML = element.innerHTML.replace(/\n<\/code><\/pre>/g, '</code></pre>')
        },
      },
    }
    return { markdown: spec }
  },
})

/** True when a `<pre>` holds a mermaid fence. */
function isMermaidFence(element: HTMLElement): boolean {
  const code = element.firstElementChild
  if (!code || code.tagName.toLowerCase() !== 'code') return false
  return Array.from(code.classList).some(
    (name) => name === `language-${MERMAID_LANGUAGE}` || name === MERMAID_LANGUAGE,
  )
}
